import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminRouteGuard from './components/AdminRouteGuard';
import { ADMIN_SECTIONS } from './utils/adminPermissions';

const AdminNetworkAnalytics = () => {
  const navigate = useNavigate();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

  const getValidToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return null;
    }
    return token;
  };

  useEffect(() => {
    fetchNetworkAnalytics();
  }, []);

  const fetchNetworkAnalytics = async () => {
    setLoading(true);
    try {
      const token = getValidToken();
      if (!token) return;

      const response = await axios.get(`${BACKEND_URL}/api/admin/network-analytics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Error fetching network analytics:', error);
      setError('Failed to load network analytics data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
              ))}
            </div>
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AdminRouteGuard requiredPermission={ADMIN_SECTIONS.ALUMNI_MANAGEMENT}>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                Alumni Network Analytics
              </h1>
              <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-300">
                Comprehensive insights into alumni networking activity and platform engagement.
              </p>
            </div>
            <button
              onClick={() => navigate('/admin')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              ← Back to Admin
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-200 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {analyticsData ? (
          <>
            {/* Key Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  {analyticsData.totalUsers || 0}
                </div>
                <div className="text-sm text-blue-600 dark:text-blue-400 font-medium mt-1">
                  Total Alumni Users
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  +{analyticsData.userGrowth || 0} this month
                </div>
              </div>
              
              <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-lg border border-green-200 dark:border-green-800">
                <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {analyticsData.totalConnections || 0}
                </div>
                <div className="text-sm text-green-600 dark:text-green-400 font-medium mt-1">
                  Total Connections
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {analyticsData.avgConnectionsPerUser || 0} avg per user
                </div>
              </div>
              
              <div className="bg-purple-50 dark:bg-purple-900/20 p-6 rounded-lg border border-purple-200 dark:border-purple-800">
                <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                  {analyticsData.totalPosts || 0}
                </div>
                <div className="text-sm text-purple-600 dark:text-purple-400 font-medium mt-1">
                  Total Posts
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {analyticsData.avgEngagementRate || 0}% engagement rate
                </div>
              </div>
              
              <div className="bg-orange-50 dark:bg-orange-900/20 p-6 rounded-lg border border-orange-200 dark:border-orange-800">
                <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                  {analyticsData.activeUsersLast30Days || 0}
                </div>
                <div className="text-sm text-orange-600 dark:text-orange-400 font-medium mt-1">
                  Active Users (30d)
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {Math.round((analyticsData.activeUsersLast30Days / analyticsData.totalUsers) * 100) || 0}% of total
                </div>
              </div>
            </div>

            {/* Activity Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Network Activity
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Connection Requests Sent</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {analyticsData.connectionRequestsSent || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Connection Acceptance Rate</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {analyticsData.connectionAcceptanceRate || 0}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Messages Sent</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {analyticsData.messagesSent || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Profile Views</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {analyticsData.profileViews || 0}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Content Engagement
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Total Likes</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {analyticsData.totalLikes || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Total Comments</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {analyticsData.totalComments || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Avg Interactions per Post</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {analyticsData.avgInteractionsPerPost || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Posts This Month</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {analyticsData.postsThisMonth || 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* University Distribution */}
            {analyticsData.universityBreakdown && analyticsData.universityBreakdown.length > 0 && (
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-8">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                  Top Universities by Alumni Connections
                </h3>
                <div className="space-y-4">
                  {analyticsData.universityBreakdown.slice(0, 10).map((university, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-300 truncate flex-1 mr-4">
                        {university.name}
                      </span>
                      <div className="flex items-center gap-3">
                        <div className="w-32 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ 
                              width: `${(university.count / analyticsData.universityBreakdown[0].count) * 100}%` 
                            }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white w-12 text-right">
                          {university.count}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Refresh Button */}
            <div className="text-center">
              <button
                onClick={fetchNetworkAnalytics}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                🔄 Refresh Data
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 dark:text-gray-500 mb-4">
              <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-4">
              No network analytics data available.
            </p>
            <button
              onClick={fetchNetworkAnalytics}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>
    </AdminRouteGuard>
  );
};

export default AdminNetworkAnalytics;